import React, { useState, useMemo } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardActions,
  Avatar,
  Typography,
  Chip,
  IconButton,
  Box,
  Grid,
  Button,
  Collapse,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Container,
  Divider,
  Tooltip,
  Paper,
} from "@mui/material";
import {
  Share,
  FavoriteBorder,
  Favorite,
  ExpandMore,
  ExpandLess, // Import ExpandLess
  Add,
  Edit,
  LocationOn,
  Water,
  ThermostatAuto,
  Map,
  PhotoCamera,
} from "@mui/icons-material";
import { styled, useTheme } from "@mui/material/styles"; // Import useTheme

// Import your existing components
import LikeButton from "../likebutton.js";
import { useAuth } from "../../contexts/AuthContext.js";
import TideTable from "../weather/tidetable.js";
import TideChart from "../weather/tidegraph.js";
import { LogDiveForm } from "../dives/LogDiveForm.js";
import { CardMediaHero } from "../media/heroimage.js";
import { CardMediaDiagram } from "../media/diagramimage.js";
import { CardMediaImages } from "../media/mediaimages.js";
import MiniGoogleMap from "../../maps/minigooglemap.js";
import AllDivesiteDetailsWeather from "../weather/alldivesiteweather.js";
import useDivesiteStats from "../../hooks/useDives.js";
import DivesLogged from "../Tiles/DivesLogged.js";
import DivesiteRatingTile from "../Tiles/DivesiteRatingTile.js";
import LastVizRatingTile from "../Tiles/LastVizRatingTile.js";
import SlogToWaterTile from "../Tiles/SlogToWaterTile.js";
import SurfaceSwimDistanceTile from "../Tiles/SurfaceSwimDistanceTile.js";
import AvgDiveRatingTile from "../Tiles/AvgDiveRatingTile.js";
import LatestDiveComments from "../Tiles/LatestDiveComments.js";
import CreatureListTile from "../Tiles/CreatureListTile.js";
import SuggestUpdateModal from "../SuggestUpdateModal.js";

const StatCard = ({ icon, value, label, color = "primary", children }) => (
  <Paper
    sx={{
      p: { xs: 1.5, sm: 2 },
      textAlign: "center",
      background:
        color === "primary"
          ? "linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(102, 126, 234, 0.05) 100%)"
          : color === "secondary"
          ? "linear-gradient(135deg, rgba(118, 75, 162, 0.1) 0%, rgba(118, 75, 162, 0.05) 100%)"
          : "rgba(255, 255, 255, 0.7)",
      backdropFilter: "blur(10px)",
      border: "1px solid rgba(0, 0, 0, 0.05)",
      borderRadius: 3,
      transition: "all 0.3s ease",
      cursor: "pointer",
      "&:hover": {
        transform: "translateY(-4px)",
        boxShadow: "0 8px 25px rgba(0, 0, 0, 0.1)",
      },
    }}
  >
    {children || (
      <>
        <Box sx={{ fontSize: { xs: "24px", sm: "32px" }, mb: 1.5 }}>{icon}</Box>
        <Typography
          variant="h4"
          fontWeight={700}
          sx={{ mb: 1, fontSize: { xs: "1.2rem", sm: "2rem" } }}
        >
          {value}
        </Typography>
        <Typography variant="caption" color="text.secondary" fontWeight={500}>
          {label}
        </Typography>
      </>
    )}
  </Paper>
);

const ModernDiveSiteCard = ({ divesite }) => {
  const [expanded, setExpanded] = useState(false);
  const [logDiveOpen, setLogDiveOpen] = useState(false);
  const [suggestUpdateOpen, setSuggestUpdateOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});

  const {
    statsWithComments,
    loading: statsLoading,
    error: statsError,
  } = useDivesiteStats(divesite?._id);

  const theme = useTheme(); // Define theme using the useTheme hook
  const { currentUser, userObjectId } = useAuth();

  // Extract data from your divesite structure
  const siteName = divesite?.uddf?.divesite?.site?.name || "Unknown Site";
  const city = divesite?.uddf?.divesite?.site?.geography?.address?.city || "";
  const province =
    divesite?.uddf?.divesite?.site?.geography?.address?.province || "";
  const provinceShort =
    divesite?.uddf?.divesite?.site?.geography?.address?.province_short || "";
  const location = divesite?.uddf?.divesite?.site?.geography?.location || "";
  const platform = divesite?.uddf?.dive?.informationbeforedive?.platform || "";
  const oneliner = divesite?.metadata?.oneliner || "";
  const siteNotes = divesite?.uddf?.divesite?.site?.notes?.para || "";
  const shoreNotes = divesite?.uddf?.divesite?.site?.shore?.notes?.para || "";
  const slogRating = divesite?.metadata?.ratings?.slog || 0;
  const surfaceSwimDistance =
    divesite?.metadata?.additionalInfo?.surfaceSwimDistance || 0;
  const closestTideStation =
    divesite?.metadata?.additionalInfo?.closestTideStation || "";
  const latitude = divesite?.uddf?.divesite?.site?.geography?.latitude;
  const longitude = divesite?.uddf?.divesite?.site?.geography?.longitude;

  const latestCommentWithViz = useMemo(() => {
    if (statsWithComments?.latestComments) {
      return statsWithComments.latestComments.find(
        (comment) =>
          comment.visibilityRating !== null &&
          comment.visibilityRating !== undefined
      );
    }
    return null;
  }, [statsWithComments?.latestComments]);

  const handleExpandClick = () => setExpanded(!expanded);
  const handleLogDiveOpen = () => setLogDiveOpen(true);
  const handleLogDiveClose = () => setLogDiveOpen(false);
  const handleSuggestUpdateOpen = () => setSuggestUpdateOpen(true);
  const handleSuggestUpdateClose = () => setSuggestUpdateOpen(false);

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const clickableStyle = {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.04)",
    },
  };

  if (!divesite) return <div>No divesite data available</div>;

  return (
    <Container
      maxWidth="md"
      sx={{
        my: { xs: 1, sm: 2, md: 4 },
        px: { xs: 0.5, sm: 2 },
      }}
    >
      <Card
        sx={{
          maxWidth: { xs: "100%", sm: "800px" },
          margin: "0 auto",
          background: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(20px)",
          borderRadius: { xs: 2, sm: 4 },
          boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
          border: "1px solid rgba(255, 255, 255, 0.2)",
          transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            transform: "translateY(-8px)",
            boxShadow: "0 30px 60px rgba(0, 0, 0, 0.15)",
          },
        }}
      >
        {/* Header */}
        <CardHeader
          avatar={
            <Box sx={{ position: "relative" }}>
              <Avatar
                sx={{
                  width: { xs: 40, sm: 56 },
                  height: { xs: 40, sm: 56 },
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  fontSize: { xs: "14px", sm: "18px" },
                  fontWeight: 700,
                  boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
                }}
              >
                {provinceShort || province?.substring(0, 2).toUpperCase()}
              </Avatar>
            </Box>
          }
          title={
            <Typography
              variant="h3"
              fontWeight={800}
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                mb: 1,
                fontSize: { xs: "1.25rem", sm: "1.5rem", md: "2rem" },
                lineHeight: 1.2,
              }}
            >
              {siteName}
            </Typography>
          }
          subheader={
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Chip
                icon={<LocationOn />}
                label={city}
                size="small"
                sx={{
                  backgroundColor: "rgba(102, 126, 234, 0.1)",
                  border: "1px solid rgba(102, 126, 234, 0.2)",
                  color: "#667eea",
                  fontSize: { xs: "0.7rem", sm: "0.8rem" },
                }}
              />
              <Chip
                icon={<Water />}
                label={`${location} ${platform}`.trim()}
                size="small"
                sx={{
                  backgroundColor: "rgba(118, 75, 162, 0.1)",
                  border: "1px solid rgba(118, 75, 162, 0.2)",
                  color: "#764ba2",
                  fontSize: { xs: "0.7rem", sm: "0.8rem" },
                }}
              />
            </Box>
          }
          action={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {currentUser && userObjectId && (
                <LikeButton
                  userId={userObjectId}
                  itemId={divesite._id}
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.8)",
                    backdropFilter: "blur(10px)",
                    color: theme.palette.error.main, // Use theme color
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 1)",
                      transform: "scale(1.05)",
                    },
                    padding: { xs: 0.5, sm: 1 },
                  }}
                />
              )}
            </Box>
          }
          sx={{
            background:
              "linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)",
            borderBottom: "1px solid rgba(0, 0, 0, 0.06)",
            padding: { xs: 1, sm: 2 },
            paddingBottom: { xs: 0.5, sm: 1 },
          }}
        />
        {/* Enhanced Hero Section */}
        <Box
          onClick={handleExpandClick}
          sx={{
            position: "relative",
            overflow: "hidden",
            borderRadius: { xs: 0, sm: "0 0 24px 24px" }, // Rounded bottom corners
            cursor: "pointer",
            transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",

            // Glass overlay that becomes more prominent on hover
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: [
                "linear-gradient(135deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.4) 100%)",
                "linear-gradient(45deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)",
              ].join(", "),
              
              transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
              zIndex: 1,
            },

            "&:hover": {
              transform: "scale(1.02)",
              "&::before": {
                backdropFilter: "blur(4px)",
                background: [
                  "linear-gradient(135deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.3) 100%)",
                  "linear-gradient(45deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)",
                ].join(", "),
              },
            },

            // Inner shadow for depth
            "&::after": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              boxShadow: "inset 0 0 60px rgba(0,0,0,0.2)",
              pointerEvents: "none",
              zIndex: 2,
            },
          }}
        >


          {/* Gradient overlay at bottom for text readability */}
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: "40%",
              background:
                "linear-gradient(transparent 0%, rgba(0,0,0,0.6) 100%)",
              zIndex: 2,
              pointerEvents: "none",
            }}
          />

          {/* Optional: Add floating stats or quick info */}
          <Box
            sx={{
              position: "absolute",
              bottom: 16,
              left: 16,
              zIndex: 3,
              display: "flex",
              gap: 1,
              flexWrap: "wrap",
            }}
          >

          </Box>

          {/* The actual hero image component */}
          <Box
            sx={{
              position: "relative",
              zIndex: 0,
              "& img": {
                transition: "transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
              },
              "&:hover img": {
                transform: "scale(1.05)",
              },
            }}
          >
            <CardMediaHero divesiteId={divesite._id} />
          </Box>
        </Box>

        {/* Main Content */}
        <CardContent
          sx={{
            padding: { xs: 1, sm: 2 },
            paddingTop: { xs: 1, sm: 2 },
          }}
        >
          {/* Description */}
          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: "0.9rem", sm: "1rem" },
              lineHeight: 1.6,
              color: "#666",
              mb: 3,
            }}
          >
            {oneliner}
          </Typography>

          {/* Stats Grid using your existing tiles */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}>
              <StatCard color="secondary">
                <SlogToWaterTile slogValue={slogRating} />
              </StatCard>
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard color="secondary">
                <SurfaceSwimDistanceTile
                  surfaceSwimDistance={surfaceSwimDistance}
                />
              </StatCard>
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard color="secondary">
                <LastVizRatingTile
                  divesiteId={divesite?._id}
                  latestVizComment={latestCommentWithViz}
                  loading={statsLoading}
                  error={statsError}
                />
              </StatCard>
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard color="secondary">
                <AvgDiveRatingTile
                  avgDiveRating={statsWithComments?.avgDiveRating}
                  divesiteId={divesite._id}
                />
              </StatCard>
            </Grid>
          </Grid>

          {/* Marine Life */}
          <Box sx={{ mb: 3 }}>
            <CreatureListTile divesiteId={divesite._id} />
          </Box>

          {/* Recent Comments */}
          <Box>
            <LatestDiveComments comments={statsWithComments?.latestComments} />
          </Box>
        </CardContent>
        {/* Action Buttons */}
        <CardActions
          disableSpacing
          sx={{
            justifyContent: "center",
            padding: { xs: 1, sm: 2 },
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 1, sm: 2 },
          }}
        >
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleLogDiveOpen}
            fullWidth={{ xs: true, sm: false }}
            sx={{
              minWidth: { sm: "200px" },
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              py: 1.5,
              fontSize: { xs: "0.85rem", sm: "1rem" },
              fontWeight: 600,
              borderRadius: 2,
              "&:hover": {
                background: "linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)",
                transform: "translateY(-2px)",
                boxShadow: "0 8px 25px rgba(102, 126, 234, 0.3)",
              },
            }}
          >
            Log a Dive
          </Button>
          <Button
            variant="outlined"
            startIcon={<Edit />}
            onClick={handleSuggestUpdateOpen}
            fullWidth={{ xs: true, sm: false }}
            sx={{
              minWidth: { sm: "150px" },
              color: "#667eea",
              borderColor: "rgba(102, 126, 234, 0.3)",
              borderWidth: 2,
              py: 1.5,
              fontSize: { xs: "0.85rem", sm: "1rem" },
              fontWeight: 600,
              borderRadius: 2,
              "&:hover": {
                borderColor: "#667eea",
                backgroundColor: "rgba(102, 126, 234, 0.1)",
                transform: "translateY(-2px)",
              },
            }}
          >
            Suggest Update
          </Button>
        </CardActions>
        {/* "More Details" Button - Placed below CardActions */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            py: { xs: 1, sm: 1.5 }, // Padding around the button
            borderTop: "1px solid rgba(0,0,0,0.08)", // Optional: separator line
            mt: 1, // Margin top to separate from CardActions
          }}
        >
          <Button
            onClick={handleExpandClick}
            startIcon={expanded ? <ExpandLess /> : <ExpandMore />}
            variant="text" // Or "outlined" if you prefer more emphasis
            sx={{
              color: "text.secondary",
              fontWeight: 500,
              fontSize: { xs: "0.8rem", sm: "0.875rem" },
              borderRadius: 2,
              py: { xs: 0.75, sm: 1 },
              px: { xs: 1.5, sm: 2 },
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
                color: "primary.main",
              },
            }}
          >
            {expanded ? "Fewer Details" : "More Details"}
          </Button>
        </Box>
        {/* Expandable Content */}
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box
            sx={{
              borderTop: "1px solid rgba(0, 0, 0, 0.1)",
              p: { xs: 1, sm: 2 },
            }}
          >
            <Stack spacing={{ xs: 2, sm: 3, md: 4 }}>
              {/* Weather and Tides - Enhanced */}
              <Accordion
                expanded={expandedSections.weather}
                onChange={() => toggleSection("weather")}
                sx={{
                  borderRadius: "16px !important",
                  overflow: "hidden",
                  background: "rgba(255, 255, 255, 0.8)",
                  backdropFilter: "blur(20px)",
                  border: "1px solid rgba(102, 126, 234, 0.1)",
                  boxShadow: "0 8px 25px rgba(0, 0, 0, 0.08)",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 12px 35px rgba(0, 0, 0, 0.12)",
                    borderColor: "rgba(102, 126, 234, 0.2)",
                  },
                  "&:before": {
                    display: "none", // Remove default MUI accordion border
                  },
                  mb: 2,
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <ExpandMore
                      sx={{
                        color: "#667eea",
                        transition: "transform 0.3s ease",
                        transform: expandedSections.weather
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                      }}
                    />
                  }
                  sx={{
                    background: expandedSections.weather
                      ? "linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%)"
                      : "linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)",
                    borderRadius: expandedSections.weather
                      ? "16px 16px 0 0"
                      : "16px",
                    transition: "all 0.3s ease",
                    minHeight: { xs: 56, sm: 64 },
                    px: { xs: 2, sm: 3 },
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)",
                    },
                    "& .MuiAccordionSummary-content": {
                      margin: "12px 0",
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "12px",
                        background:
                          "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
                      }}
                    >
                      <ThermostatAuto sx={{ fontSize: 20, color: "white" }} />
                    </Box>
                    <Box>
                      <Typography
                        variant="h6"
                        fontWeight={700}
                        sx={{
                          background:
                            "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                          backgroundClip: "text",
                          fontSize: { xs: "1rem", sm: "1.1rem" },
                        }}
                      >
                        Weather & Tides
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 500 }}
                      >
                        Current conditions and forecasts
                      </Typography>
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    p: { xs: 2, sm: 3 },
                    background: "rgba(255, 255, 255, 0.4)",
                    backdropFilter: "blur(10px)",
                  }}
                >
                  <Grid container spacing={{ xs: 2, sm: 3 }}>
                    <Grid item xs={12} md={6}>
                      <Box
                        sx={{
                          background: "rgba(255, 255, 255, 0.7)",
                          borderRadius: 3,
                          p: 2,
                          border: "1px solid rgba(0, 0, 0, 0.05)",
                        }}
                      >
                        <AllDivesiteDetailsWeather divesiteId={divesite._id} />
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box
                        sx={{
                          background: "rgba(255, 255, 255, 0.7)",
                          borderRadius: 3,
                          p: 2,
                          border: "1px solid rgba(0, 0, 0, 0.05)",
                        }}
                      >
                        <TideChart station={closestTideStation} />
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          background: "rgba(255, 255, 255, 0.7)",
                          borderRadius: 3,
                          p: 2,
                          border: "1px solid rgba(0, 0, 0, 0.05)",
                        }}
                      >
                        <TideTable station={closestTideStation} />
                      </Box>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
              {/* Site Description - Enhanced */}
              {(siteNotes || shoreNotes) && (
                <Accordion
                  expanded={expandedSections.description}
                  onChange={() => toggleSection("description")}
                  sx={{
                    borderRadius: "16px !important",
                    overflow: "hidden",
                    background: "rgba(255, 255, 255, 0.8)",
                    backdropFilter: "blur(20px)",
                    border: "1px solid rgba(118, 75, 162, 0.1)",
                    boxShadow: "0 8px 25px rgba(0, 0, 0, 0.08)",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 12px 35px rgba(0, 0, 0, 0.12)",
                      borderColor: "rgba(118, 75, 162, 0.2)",
                    },
                    "&:before": {
                      display: "none",
                    },
                    mb: 2,
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <ExpandMore
                        sx={{
                          color: "#764ba2",
                          transition: "transform 0.3s ease",
                          transform: expandedSections.description
                            ? "rotate(180deg)"
                            : "rotate(0deg)",
                        }}
                      />
                    }
                    sx={{
                      background: expandedSections.description
                        ? "linear-gradient(135deg, rgba(118, 75, 162, 0.08) 0%, rgba(102, 126, 234, 0.08) 100%)"
                        : "linear-gradient(135deg, rgba(118, 75, 162, 0.05) 0%, rgba(102, 126, 234, 0.05) 100%)",
                      borderRadius: expandedSections.description
                        ? "16px 16px 0 0"
                        : "16px",
                      transition: "all 0.3s ease",
                      minHeight: { xs: 56, sm: 64 },
                      px: { xs: 2, sm: 3 },
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, rgba(118, 75, 162, 0.1) 0%, rgba(102, 126, 234, 0.1) 100%)",
                      },
                      "& .MuiAccordionSummary-content": {
                        margin: "12px 0",
                      },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "12px",
                          background:
                            "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: "0 4px 12px rgba(118, 75, 162, 0.3)",
                        }}
                      >
                        <Typography
                          sx={{ fontSize: 20, color: "white", fontWeight: 700 }}
                        >
                          📝
                        </Typography>
                      </Box>
                      <Box>
                        <Typography
                          variant="h6"
                          fontWeight={700}
                          sx={{
                            background:
                              "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            backgroundClip: "text",
                            fontSize: { xs: "1rem", sm: "1.1rem" },
                          }}
                        >
                          Site Description
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontWeight: 500 }}
                        >
                          Detailed dive site information
                        </Typography>
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{
                      p: { xs: 2, sm: 3 },
                      background: "rgba(255, 255, 255, 0.4)",
                      backdropFilter: "blur(10px)",
                    }}
                  >
                    <Stack spacing={3}>
                      {shoreNotes && (
                        <Box
                          sx={{
                            background: "rgba(255, 255, 255, 0.7)",
                            borderRadius: 3,
                            p: 3,
                            border: "1px solid rgba(0, 0, 0, 0.05)",
                            position: "relative",
                            "&::before": {
                              content: '""',
                              position: "absolute",
                              left: 0,
                              top: 0,
                              bottom: 0,
                              width: 4,
                              background:
                                "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                              borderRadius: "0 4px 4px 0",
                            },
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            fontWeight={700}
                            sx={{
                              mb: 2,
                              color: "#667eea",
                              fontSize: { xs: "0.95rem", sm: "1rem" },
                            }}
                          >
                            About the dive
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              lineHeight: 1.7,
                              color: "#555",
                              fontSize: { xs: "0.85rem", sm: "0.9rem" },
                            }}
                          >
                            {shoreNotes}
                          </Typography>
                        </Box>
                      )}
                      {siteNotes && (
                        <Box
                          sx={{
                            background: "rgba(255, 255, 255, 0.7)",
                            borderRadius: 3,
                            p: 3,
                            border: "1px solid rgba(0, 0, 0, 0.05)",
                            position: "relative",
                            "&::before": {
                              content: '""',
                              position: "absolute",
                              left: 0,
                              top: 0,
                              bottom: 0,
                              width: 4,
                              background:
                                "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
                              borderRadius: "0 4px 4px 0",
                            },
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            fontWeight={700}
                            sx={{
                              mb: 2,
                              color: "#764ba2",
                              fontSize: { xs: "0.95rem", sm: "1rem" },
                            }}
                          >
                            How to dive it
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              lineHeight: 1.7,
                              color: "#555",
                              fontSize: { xs: "0.85rem", sm: "0.9rem" },
                            }}
                          >
                            {siteNotes}
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              )}
              {/* Map - Enhanced */}
              <Accordion
                expanded={expandedSections.map}
                onChange={() => toggleSection("map")}
                sx={{
                  borderRadius: "16px !important",
                  overflow: "hidden",
                  background: "rgba(255, 255, 255, 0.8)",
                  backdropFilter: "blur(20px)",
                  border: "1px solid rgba(76, 175, 80, 0.1)",
                  boxShadow: "0 8px 25px rgba(0, 0, 0, 0.08)",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 12px 35px rgba(0, 0, 0, 0.12)",
                    borderColor: "rgba(76, 175, 80, 0.2)",
                  },
                  "&:before": {
                    display: "none",
                  },
                  mb: 2,
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <ExpandMore
                      sx={{
                        color: "#4CAF50",
                        transition: "transform 0.3s ease",
                        transform: expandedSections.map
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                      }}
                    />
                  }
                  sx={{
                    background: expandedSections.map
                      ? "linear-gradient(135deg, rgba(76, 175, 80, 0.08) 0%, rgba(102, 126, 234, 0.08) 100%)"
                      : "linear-gradient(135deg, rgba(76, 175, 80, 0.05) 0%, rgba(102, 126, 234, 0.05) 100%)",
                    borderRadius: expandedSections.map
                      ? "16px 16px 0 0"
                      : "16px",
                    transition: "all 0.3s ease",
                    minHeight: { xs: 56, sm: 64 },
                    px: { xs: 2, sm: 3 },
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(102, 126, 234, 0.1) 100%)",
                    },
                    "& .MuiAccordionSummary-content": {
                      margin: "12px 0",
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "12px",
                        background:
                          "linear-gradient(135deg, #4CAF50 0%, #667eea 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 4px 12px rgba(76, 175, 80, 0.3)",
                      }}
                    >
                      <Map sx={{ fontSize: 20, color: "white" }} />
                    </Box>
                    <Box>
                      <Typography
                        variant="h6"
                        fontWeight={700}
                        sx={{
                          background:
                            "linear-gradient(135deg, #4CAF50 0%, #667eea 100%)",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                          backgroundClip: "text",
                          fontSize: { xs: "1rem", sm: "1.1rem" },
                        }}
                      >
                        Dive Site Map
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 500 }}
                      >
                        Location and coordinates
                      </Typography>
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    p: { xs: 2, sm: 3 },
                    background: "rgba(255, 255, 255, 0.4)",
                    backdropFilter: "blur(10px)",
                  }}
                >
                  <Box
                    sx={{
                      borderRadius: 3,
                      overflow: "hidden",
                      background: "rgba(255, 255, 255, 0.7)",
                      border: "1px solid rgba(0, 0, 0, 0.05)",
                      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                      "& > *": {
                        height: { xs: "250px", sm: "300px", md: "400px" },
                      },
                    }}
                  >
                    <MiniGoogleMap
                      lat={parseFloat(latitude?.toFixed(4))}
                      lng={parseFloat(longitude?.toFixed(4))}
                      markerName={siteName}
                    />
                  </Box>
                  <Box
                    sx={{ display: "flex", justifyContent: "center", mt: 3 }}
                  >
                    <Chip
                      icon={<LocationOn />}
                      label={`${latitude?.toFixed(4)}, ${longitude?.toFixed(
                        4
                      )}`}
                      sx={{
                        backgroundColor: "rgba(76, 175, 80, 0.1)",
                        color: "#4CAF50",
                        border: "2px solid rgba(76, 175, 80, 0.3)",
                        fontWeight: 600,
                        py: 2,
                        px: 1,
                        fontSize: "0.9rem",
                        borderRadius: 3,
                        boxShadow: "0 4px 12px rgba(76, 175, 80, 0.2)",
                      }}
                    />
                  </Box>
                </AccordionDetails>
              </Accordion>
              {/* Photo Gallery - Enhanced */}
              <Accordion
                expanded={expandedSections.photos}
                onChange={() => toggleSection("photos")}
                sx={{
                  borderRadius: "16px !important",
                  overflow: "hidden",
                  background: "rgba(255, 255, 255, 0.8)",
                  backdropFilter: "blur(20px)",
                  border: "1px solid rgba(255, 152, 0, 0.1)",
                  boxShadow: "0 8px 25px rgba(0, 0, 0, 0.08)",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 12px 35px rgba(0, 0, 0, 0.12)",
                    borderColor: "rgba(255, 152, 0, 0.2)",
                  },
                  "&:before": {
                    display: "none",
                  },
                  mb: 2,
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <ExpandMore
                      sx={{
                        color: "#FF9800",
                        transition: "transform 0.3s ease",
                        transform: expandedSections.photos
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                      }}
                    />
                  }
                  sx={{
                    background: expandedSections.photos
                      ? "linear-gradient(135deg, rgba(255, 152, 0, 0.08) 0%, rgba(102, 126, 234, 0.08) 100%)"
                      : "linear-gradient(135deg, rgba(255, 152, 0, 0.05) 0%, rgba(102, 126, 234, 0.05) 100%)",
                    borderRadius: expandedSections.photos
                      ? "16px 16px 0 0"
                      : "16px",
                    transition: "all 0.3s ease",
                    minHeight: { xs: 56, sm: 64 },
                    px: { xs: 2, sm: 3 },
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, rgba(255, 152, 0, 0.1) 0%, rgba(102, 126, 234, 0.1) 100%)",
                    },
                    "& .MuiAccordionSummary-content": {
                      margin: "12px 0",
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "12px",
                        background:
                          "linear-gradient(135deg, #FF9800 0%, #667eea 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 4px 12px rgba(255, 152, 0, 0.3)",
                      }}
                    >
                      <PhotoCamera sx={{ fontSize: 20, color: "white" }} />
                    </Box>
                    <Box>
                      <Typography
                        variant="h6"
                        fontWeight={700}
                        sx={{
                          background:
                            "linear-gradient(135deg, #FF9800 0%, #667eea 100%)",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                          backgroundClip: "text",
                          fontSize: { xs: "1rem", sm: "1.1rem" },
                        }}
                      >
                        Photos & Diagrams
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 500 }}
                      >
                        Visual guides and site images
                      </Typography>
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    p: { xs: 2, sm: 3 },
                    background: "rgba(255, 255, 255, 0.4)",
                    backdropFilter: "blur(10px)",
                  }}
                >
                  <Stack spacing={4}>
                    <Box>
                      <Typography
                        variant="subtitle1"
                        fontWeight={700}
                        sx={{
                          mb: 2,
                          color: "#FF9800",
                          fontSize: { xs: "0.95rem", sm: "1rem" },
                        }}
                      >
                        Site Images
                      </Typography>
                      <Box
                        sx={{
                          background: "rgba(255, 255, 255, 0.7)",
                          borderRadius: 3,
                          p: 2,
                          border: "1px solid rgba(0, 0, 0, 0.05)",
                        }}
                      >
                        <CardMediaImages divesiteId={divesite._id} />
                      </Box>
                    </Box>
                    <Box>
                      <Typography
                        variant="subtitle1"
                        fontWeight={700}
                        sx={{
                          mb: 2,
                          color: "#667eea",
                          fontSize: { xs: "0.95rem", sm: "1rem" },
                        }}
                      >
                        Site Diagram
                      </Typography>
                      <Box
                        sx={{
                          background: "rgba(255, 255, 255, 0.7)",
                          borderRadius: 3,
                          p: 2,
                          border: "1px solid rgba(0, 0, 0, 0.05)",
                        }}
                      >
                        <CardMediaDiagram divesiteId={divesite._id} />
                      </Box>
                    </Box>
                  </Stack>
                </AccordionDetails>
              </Accordion>
            </Stack>
          </Box>
        </Collapse>
      </Card>

      {/* Dialogs */}
      <Dialog open={logDiveOpen} onClose={handleLogDiveClose} maxWidth="md">
        <DialogTitle sx={{ fontSize: { xs: "1.1rem", sm: "1.25rem" } }}>
          Log a dive at {siteName}
        </DialogTitle>
        <DialogContent>
          {/* HERE'S THE IMPORTANT CHANGE: Pass onSubmitSuccess prop */}
          <LogDiveForm
            divesiteid={divesite._id}
            onSubmitSuccess={handleLogDiveClose} // Pass the close function here
          />
        </DialogContent>
        <DialogActions sx={{ padding: { xs: 1, sm: 2 } }}>
          <Button onClick={handleLogDiveClose}>Close</Button>
        </DialogActions>
      </Dialog>

      <SuggestUpdateModal
        open={suggestUpdateOpen}
        onClose={handleSuggestUpdateClose}
        divesiteId={divesite._id}
      />
    </Container>
  );
};

export default ModernDiveSiteCard;
